import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { useSettings } from 'services/settings/SettingsContext';
import { useFetchData } from 'services/api/useFetchData';
import { useModal } from 'components/modals/ModalContext';
import { ReleaseType } from './ReleaseTypes';
import { saveData } from 'services/api/saveData';
import FormFieldContext from 'components/forms/FormFieldContext';
import TextInput from 'components/forms/basefields/TextInput';
import DateInput from 'components/forms/basefields/DateInput';
import Dropdown from 'components/forms/basefields/Dropdown';
import ReleaseItemsRowsField from 'components/forms/fieldsets/ReleaseItemsRowsField';
import SecondaryButton from 'components/buttons/SecondaryButton';
import PrimaryButton from 'components/buttons/PrimaryButton';
import ReleaseEmailPreview from './ReleaseEmailPreview';
import CheckboxCheck from 'assets/CheckboxCheck.svg';
import '../../style/scss/forms.scss';

const ReleaseEditForm: React.FC<{ itemId?: number, viewKey?: string }> = ({ itemId, viewKey }) => {
    const { t } = useTranslation();
    const { errorMessages, setFloatingAlert, setUnsavedChanges, unsavedChangesMap } = useGlobalContext();
    const { userLocale } = useSettings();
    const { initializeModal, revealModal, closeModal } = useModal();
    const [originalData, setOriginalData] = useState<ReleaseType | null>(null);
    const [data, setData] = useState<ReleaseType | null>(null);
    const [updatedData, setUpdatedData] = useState<Record<string, any>>({});
    const [buttonLoader, setButtonLoader] = useState(false);
    const releaseStatusOptions: { value: ReleaseType['status']; name: string }[] = [
        { value: 'draft', name: 'release.general.draft_release_label'},
        { value: 'published', name: 'release.general.published_release_label'}]
    const editing = true;

    // Fetch the data of the current email
    const { response } = useFetchData({ 
        apiUrl: itemId ? `get_release/${itemId}` : '',
        guardValue: itemId,
        useGuard: true
    });

    // Set the current data and reveal the modal
    useEffect(() => {
        if (response) {
            // Copy the release date value to the release date input field
            const copiedResponse = {
                ...response,
                release_date_input: response.release_date
            };

            // Set the data
            setData(copiedResponse);
            setOriginalData(copiedResponse);
            revealModal();
        }
    }, [response]);

    // Set unsaved changes when updated data changes
    useEffect(() => {
        if (!originalData || !updatedData) return;

        // Check if the updated data contains different values than the original data
        const hasChanges = Object.entries(updatedData).some(([key, value]) => {
            return originalData[key as keyof ReleaseType] !== value;
        });

        // Set unsaved changes
        if (viewKey && (unsavedChangesMap[viewKey] !== hasChanges)) {
            setUnsavedChanges(viewKey, hasChanges);
        }
    }, [originalData, updatedData]);

    // Handle input change
    const handleInputChange = (value: string, fieldName: 'title' | 'version') => {
        setData(prev => (prev ? { ...prev, [fieldName]: value } : prev));
        setUpdatedData((prev: ReleaseType) => ({ ...prev, [fieldName]: value }));
    };

    // Handle the dropdown change
    const handleDropdownChange = (selectedValue: ReleaseType['status']) => {
        setData(prev => (prev ? { ...prev, status: selectedValue } : prev));
        setUpdatedData((prev: ReleaseType) => ({ ...prev, status: selectedValue }));
    };

    // Handles the date change
    const handleDateChange = (dateString: string | null) => {
        if (dateString === null) {
            setData(prev => (prev ? { ...prev, release_date_input: null } : prev));
            setUpdatedData((prev: ReleaseType) => ({ ...prev, release_date: null }));
        } else {
            const parsedDate = new Date(dateString);
            if (!isNaN(parsedDate.getTime())) {
                const isoDate = parsedDate.toISOString();
                setData(prev => (prev ? { ...prev, release_date_input: isoDate } : prev));
                setUpdatedData((prev: ReleaseType) => ({ ...prev, release_date: isoDate }));
            }
        }
    };

    // Handle email checkbox change
    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        setData(prev => (prev ? { ...prev, send_email_notification: checked } : prev));
        setUpdatedData((prev: ReleaseType) => ({ ...prev, send_email_notification: checked }));
    };

    // Handle submit
    const handleSubmit = async () => {
        try {
            setButtonLoader(true);

            // Strip the local image previews from the items to keep the request body small
            const payload: any = { ...updatedData };
            if (Array.isArray(payload.release_items)) {
                payload.release_items = payload.release_items.map(({ imageExample, ...row }: any) => row);
            }

            // Save the data in the backend
            const response = await saveData({
                apiUrl: `patch_release/${itemId}`,
                method: 'patch',
                data: payload,
                source: axios.CancelToken.source()
            });

            // Handle successful response
            if (response?.status === 200) {
                setButtonLoader(false);
                
                // If the release email preview is given, show it in a modal
                if (response.data.email_preview) {
                    initializeModal(
                        <ReleaseEmailPreview 
                            htmlContent={response.data.email_preview} 
                            sendMailButton={{
                                showButton: true,
                                apiUrl: `send_release_email/${response.data.release_id}`,
                                sendTestMail: true
                            }}
                        />, { modalSize: 'medium-large' }
                    )
                }
                
                // Otherwise just close the modal
                else {
                    setFloatingAlert({ 'type': 'success' });
                    if (viewKey) setUnsavedChanges(viewKey, false);
                    closeModal();
                }
            }
        } catch (error: any) {
            setFloatingAlert({ type: 'danger', message: 'validation.general.save_failed' })
            setButtonLoader(false);
        }
    };

    // Handles the closing of the modal
    const handleClose = () => {
        setButtonLoader(false);
        if (viewKey) setUnsavedChanges(viewKey, false);
        closeModal();
    };

    // Format date value
    const formatDateValue = (dateValue: string | null) => {
        if (!dateValue) {
            return '';
        } else {
            return new Date(dateValue).toLocaleDateString(userLocale, { year: 'numeric', month: '2-digit', day: '2-digit' })
        }
    };
    
    return (
        data && (
            <FormFieldContext.Provider value={{ editing, updatedData, setUpdatedData }}>
                <div className='release-edit-modal'>
                    <div className='header'>
                        <h4>
                            {t('release.general.edit_release_title')}
                        </h4>
                    </div>
                    <div className='properties'>
                        <div>
                            <label>
                                {t('release.general.title_label')}
                            </label>
                            <TextInput
                                name='title'
                                value={data.title}
                                placeholder='release.general.release_title_placeholder'
                                onChange={(value) => handleInputChange(value, 'title')}
                                shouldAutoFocus={true}
                                isInvalid={errorMessages['title']}
                            />
                        </div>
                        <div>
                            <label>
                                {t('release.general.version_number_label')}
                            </label>
                            <TextInput
                                name='version'
                                value={data.version}
                                placeholder='release.general.version_placeholder'
                                onChange={(value) => handleInputChange(value, 'version')}
                                shouldAutoFocus={true}
                                isInvalid={errorMessages['version']}
                            />
                        </div>
                        <div>
                            <label>
                                {t('release.general.release_date_label')}
                            </label>
                            <DateInput
                                id='release_date'
                                name='release_date'
                                value={data.release_date_input ? new Date(data.release_date_input) : undefined}
                                localDatetime={data.release_date_input ?? undefined}
                                onChange={(selectedValue) => handleDateChange(selectedValue)} 
                                isInvalid={errorMessages['release_date'] ? true : false}
                            />
                        </div>
                        <div>
                            <label>
                                {t('release.general.status_label')}
                            </label>
                            <Dropdown<{ value: ReleaseType['status']; name: string }>
                                options={releaseStatusOptions}
                                id='status'
                                name='status'
                                disabled_selected={t('forms.select_type_disabled_selected')}
                                selectedOption={releaseStatusOptions.find(status => status.value === data?.status)}
                                onChange={(selectedValue) => handleDropdownChange(selectedValue as ReleaseType['status'])}
                                selectionFormat={(option) => option.name}
                                optionFormat={(option) => option.name}
                                showSearch={false}
                                allowNoneOption={false}
                            />
                        </div>
                    </div>
                    <div className='release-items'>
                        <h6>
                            {t('release.general.release_items_title')}
                        </h6>
                        {viewKey && (
                            <ReleaseItemsRowsField
                                viewKey={viewKey} 
                                data={data}
                                name='release_items'
                                type='release-items'
                            />
                        )}
                    </div>
                    <div className='footer'>
                        <div className='left-content'>
                            {data.email_message ? (
                                <p>
                                    {t('release.general.email_send_description', { date: formatDateValue(data.email_sent_at) } )}
                                </p>
                            ) : (
                                <div className='checkbox-item left'>
                                    <div className="label-description-container">
                                        <label htmlFor='checkbox' className='checkbox-label'>
                                            {t('release.general.send_notification_email')}
                                        </label>
                                    </div>
                                    <div className='checkbox-wrapper'>
                                        <input 
                                            type='checkbox'
                                            id='checkbox'
                                            checked={data.send_email_notification}
                                            onChange={(event) => handleCheckboxChange(event)}
                                            className='hidden-checkbox'
                                        />
                                        <label htmlFor='checkbox'
                                            className={`custom-checkbox left ${data.send_email_notification === true ? 'checked' : ''}`}>
                                            {data.send_email_notification === true && 
                                                <img src={CheckboxCheck} alt="CheckboxCheck" className='checkbox-check' />
                                            }
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className='right-content'>
                            <SecondaryButton
                                onClick={handleClose} 
                                label="general.cancel"
                                size='small'
                            />
                            <PrimaryButton
                                onClick={handleSubmit}
                                label={data.send_email_notification ? "release.general.save_and_send" : "general.save"}
                                size='small'
                                loading={buttonLoader}
                            />
                        </div>
                    </div>
                </div>
            </FormFieldContext.Provider>
        )
    );
};

export default ReleaseEditForm;